import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../utils/date";
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import EntityLink from "../components/EntityLink";
import useModules from "../hooks/useModules";
import { useFinanceScope } from "../contexts/FinanceScopeContext";
import { api, formatApiError } from "../lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { rowOpenProps } from "../hooks/useRowOpen";
import DetailDialog from "../components/DetailDialog";
import DateInput from "../components/DateInput";

const ALL = "__all__";
const FINANCE_ENTITY_TYPES = ["payroll_period", "payroll_line", "payroll_funding", "wage_rate", "settings"];

export default function Audit() {
    const navigate = useNavigate();
    const financeScope = useFinanceScope();
    const { byId: modulesById } = useModules();
    const [logs, setLogs] = useState([]);
    const [detail, setDetail] = useState(null);
    const [actions, setActions] = useState([]);
    const [entityTypes, setEntityTypes] = useState([]);
    const [filters, setFilters] = useState({
        action: ALL, entity_type: ALL, date_from: "", date_to: "", search: "",
    });
    const [loading, setLoading] = useState(true);

    // Restrict entity_type dropdown to the finance whitelist when inside MasterFinanceShell.
    const visibleEntityTypes = useMemo(
        () => financeScope ? entityTypes.filter((t) => FINANCE_ENTITY_TYPES.includes(t)) : entityTypes,
        [entityTypes, financeScope],
    );

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 300 };
            if (filters.action !== ALL) params.action = filters.action;
            if (filters.entity_type !== ALL) {
                params.entity_type = filters.entity_type;
            } else if (financeScope) {
                // No specific entity_type chosen — restrict to finance whitelist server-side.
                params.entity_type = FINANCE_ENTITY_TYPES.join(",");
            }
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            if (filters.search) params.search = filters.search;
            const { data } = await api.get("/audit", { params });
            setLogs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        Promise.all([api.get("/audit/actions"), api.get("/audit/entity-types")])
            .then(([a, e]) => { setActions(a.data); setEntityTypes(e.data); })
            .catch(() => {});
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload whenever filters change (debounced for search)
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.action, filters.entity_type, filters.date_from, filters.date_to, filters.search, financeScope]);

    const reset = () => setFilters({ action: ALL, entity_type: ALL, date_from: "", date_to: "", search: "" });

    return (
        <AppShell title="Audit Log">
            <section className="space-y-5" data-testid="audit-page">
                <div className="asl-card p-4 flex flex-wrap items-end gap-3" data-testid="audit-filters">
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">Action</div>
                        <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
                            <SelectTrigger className="w-44" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL} data-testid="audit-filter-all">All actions</SelectItem>
                                <SelectItem value="*_delete_soft" data-testid="audit-filter-soft-delete">Soft delete (any entity)</SelectItem>
                                <SelectItem value="*_delete_hard" data-testid="audit-filter-hard-delete">Hard delete (any entity)</SelectItem>
                                <SelectItem value="*_restore" data-testid="audit-filter-restore">Restore (any entity)</SelectItem>
                                <SelectItem value="permission_grant,permission_revoke" data-testid="audit-filter-perm">Permission grant/revoke</SelectItem>
                                {actions.map((a) => <SelectItem key={a} value={a} data-testid={`audit-filter-${a}`}>{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">Entity type</div>
                        <Select value={filters.entity_type} onValueChange={(v) => setFilters((f) => ({ ...f, entity_type: v }))}>
                            <SelectTrigger className="w-44" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All types</SelectItem>
                                {visibleEntityTypes.map((t) => <SelectItem key={t} value={t} data-testid={`audit-entity-${t}`}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">From</div>
                        <DateInput className="w-40" value={filters.date_from}
                            onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))} data-testid="audit-from" />
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase">To</div>
                        <DateInput className="w-40" value={filters.date_to}
                            onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))} data-testid="audit-to" />
                    </div>
                    <div className="space-y-1 flex-1 min-w-[200px] max-w-md">
                        <div className="text-[10px] uppercase">Search (actor or action)</div>
                        <Input value={filters.search} placeholder="e.g. hr@kaafi.com or login"
                            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="audit-search" />
                    </div>
                    <Button variant="outline" size="sm" onClick={reset} data-testid="audit-reset">Reset</Button>
                </div>

                <div className="asl-card overflow-hidden">
                    {loading ? (
                        <div className="p-6 text-sm text-[color:var(--asl-muted)]" data-testid="audit-loading">Loading audit entries…</div>
                    ) : logs.length === 0 ? (
                        <div className="p-6 text-sm text-[color:var(--asl-muted)]" data-testid="audit-empty">No audit entries.</div>
                    ) : (
                        <table className="asl-table asl-table--zebra" data-testid="audit-table">
                            <thead
                                className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}
                            >
                                <tr>
                                    <th className="text-left px-5 py-3">Timestamp</th>
                                    <th className="text-left px-5 py-3">Action</th>
                                    <th className="text-left px-5 py-3">Actor</th>
                                    <th className="text-left px-5 py-3">Entity</th>
                                    <th className="text-left px-5 py-3">IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((l) => (
                                    <tr key={l.id} {...rowOpenProps(navigate, () => setDetail(l), { testid: `audit-row-${l.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                        <td className="px-5 py-3 mono text-xs text-[color:var(--asl-muted)]">{formatDateTime(l.timestamp)}</td>
                                        <td className="px-5 py-3">
                                            <span className="text-xs px-2.5 py-1 rounded-full font-medium mono"
                                                style={{
                                                    background: l.action.includes("fail") ? "rgba(168,50,74,0.1)" : "var(--asl-navy-50)",
                                                    color: l.action.includes("fail") ? "var(--asl-crimson)" : "var(--asl-navy)",
                                                }}>{l.action}</span>
                                        </td>
                                        <td className="px-5 py-3 mono text-[13px]">{l.actor_email || "—"}</td>
                                        <td className="px-5 py-3 text-xs">
                                            {l.entity_type ? (() => {
                                                let to = null;
                                                if (l.entity_type === "module" && l.entity_id) {
                                                    const mc = modulesById?.[l.entity_id]?.code;
                                                    if (mc) to = `/m/${mc.toLowerCase()}`;
                                                }
                                                if (l.entity_type === "payroll_period" && l.entity_id && l.before?.module_id) {
                                                    const mc = modulesById?.[l.before.module_id]?.code;
                                                    if (mc) to = `/m/${mc.toLowerCase()}/payroll/${l.entity_id}`;
                                                }
                                                const content = (
                                                    <>
                                                        <span className="asl-navy-text font-medium">{l.entity_type}</span>
                                                        {l.entity_id && <span className="text-[color:var(--asl-muted)] mono ml-1">· {l.entity_id.slice(0, 8)}</span>}
                                                    </>
                                                );
                                                return to ? (
                                                    <span data-no-row-click>
                                                        <EntityLink to={to} testid={`audit-entity-link-${l.id}`}>
                                                            {content}
                                                        </EntityLink>
                                                    </span>
                                                ) : <span>{content}</span>;
                                            })() : "—"}
                                        </td>
                                        <td className="px-5 py-3 mono text-xs text-[color:var(--asl-muted)]">{l.ip || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>
        
            <DetailDialog row={detail} onClose={() => setDetail(null)} title="Audit Entry" testid="detail-detail" />
</AppShell>
    );
}
